import { track } from './mixpanel';

/**
 * Centralized, typed event helpers for Mixpanel.
 *
 * Keeping event names and property shapes in one place avoids typos and keeps
 * the tracking taxonomy consistent across the app.
 */

// ===== Signup funnel =====

export const trackSignupStarted = () => {
  track('Signup Started');
};

export const trackSignupStepCompleted = (stepNumber: number, stepName: string) => {
  track('Signup Step Completed', { step_number: stepNumber, step_name: stepName });
};

export const trackSignupOtpSent = () => {
  track('Signup OTP Sent');
};

export const trackSignupOtpFailed = (reason?: string) => {
  track('Signup OTP Failed', { reason });
};

export const trackSignupCompleted = (method = 'email') => {
  track('Signup Completed', { method });
};

export const trackSignupFailed = (errorCode?: string | number) => {
  track('Signup Failed', { error_code: errorCode });
};

// ===== Page views (for funnels) =====

export const trackTasksPageViewed = (props: { orgId?: string }) => {
  track('Tasks Page Viewed', { org_id: props.orgId });
};

export const trackChatPageViewed = (props: { orgId?: string }) => {
  track('Chat Page Viewed', { org_id: props.orgId });
};

export const trackReportsPageViewed = (props: {
  orgId?: string;
  tab?: string;
  year?: number;
}) => {
  track('Reports Page Viewed', {
    org_id: props.orgId,
    tab: props.tab,
    year: props.year,
  });
};

export const trackReportTabChanged = (props: {
  orgId: string;
  tab: string;
  year: number;
  previousTab?: string;
}) => {
  track('Report Tab Changed', {
    org_id: props.orgId,
    tab: props.tab,
    year: props.year,
    previous_tab: props.previousTab,
  });
};

export const trackReportDownloaded = (props: {
  orgId: string;
  reportId: string;
  tab?: string;
  year?: number;
  reportType?: string;
}) => {
  track('Report Downloaded', {
    org_id: props.orgId,
    report_id: props.reportId,
    tab: props.tab,
    year: props.year,
    report_type: props.reportType,
  });
};

// ===== Tasks =====

export type TaskSubmissionMethod = 'file' | 'credentials' | 'schedule_meeting' | 'vat_certificate' | 'vat_no_certificate';

export const trackTaskSubmitted = (props: {
  orgId: string;
  templateKey?: string;
  taskType?: string;
  isOverdue?: boolean;
  hadFileUpload?: boolean;
  filesCount?: number;
  submissionMethod: TaskSubmissionMethod;
}) => {
  track('Task Submitted', {
    org_id: props.orgId,
    template_key: props.templateKey,
    task_type: props.taskType,
    is_overdue: props.isOverdue,
    had_file_upload: props.hadFileUpload,
    files_count: props.filesCount,
    submission_method: props.submissionMethod,
  });
};

export const trackTaskCreated = (props: {
  orgId: string;
  actorRole?: string;
  title?: string;
}) => {
  track('Task Created', {
    org_id: props.orgId,
    actor_role: props.actorRole,
    title: props.title,
  });
};

export type TaskSubmitDialogType = 'submit' | 'credentials';

export const trackTaskSubmitDialogOpened = (props: {
  orgId: string;
  templateKey?: string;
  taskType?: string;
  dialogType: TaskSubmitDialogType;
}) => {
  track('Task Submit Dialog Opened', {
    org_id: props.orgId,
    template_key: props.templateKey,
    task_type: props.taskType,
    dialog_type: props.dialogType,
  });
};

export const trackTaskFileAdded = (props: {
  orgId: string;
  templateKey?: string;
  taskType?: string;
  filesCount: number;
}) => {
  track('Task File Added', {
    org_id: props.orgId,
    template_key: props.templateKey,
    task_type: props.taskType,
    files_count: props.filesCount,
  });
};

export const trackTaskSubmitDialogCancelled = (props: {
  orgId: string;
  templateKey?: string;
  taskType?: string;
  dialogType: TaskSubmitDialogType;
  hadPendingFiles?: boolean;
}) => {
  track('Task Submit Dialog Cancelled', {
    org_id: props.orgId,
    template_key: props.templateKey,
    task_type: props.taskType,
    dialog_type: props.dialogType,
    had_pending_files: props.hadPendingFiles,
  });
};

// ===== Chat =====

export type ChatMessageType = 'text' | 'voice' | 'file';

export const trackChatMessageSent = (props: {
  orgId: string;
  messageType: ChatMessageType;
  isQuoteReply: boolean;
  isThreadReply: boolean;
  hasMentions: boolean;
  voiceDuration?: number | null;
}) => {
  track('Chat Message Sent', {
    org_id: props.orgId,
    message_type: props.messageType,
    is_quote_reply: props.isQuoteReply,
    is_thread_reply: props.isThreadReply,
    has_mentions: props.hasMentions,
    voice_duration: props.voiceDuration ?? undefined,
  });
};

// ===== Subscription =====

export type SubscribeDestination = 'plans' | 'whatsapp';

export const trackSubscribeBannerClicked = (props: {
  destination: SubscribeDestination;
  orgId?: string;
}) => {
  track('Subscribe Banner Clicked', {
    destination: props.destination,
    org_id: props.orgId,
  });
};

export const trackSubscriptionPlansViewed = (props: {
  orgId?: string;
  source?: string;
}) => {
  track('Subscription Plans Viewed', {
    org_id: props.orgId,
    source: props.source,
  });
};

export const trackCheckoutStarted = (props: {
  orgId?: string;
  planCode?: string;
  billingCycle?: string;
  installments?: number;
  price?: string | number;
}) => {
  track('Checkout Started', {
    org_id: props.orgId,
    plan_code: props.planCode,
    billing_cycle: props.billingCycle,
    installments: props.installments,
    price: props.price,
  });
};

export const trackSubscriptionCompleted = (props: {
  orgId?: string;
  planCode?: string;
  billingCycle?: string;
  amount?: string | number;
}) => {
  track('Subscription Completed', {
    org_id: props.orgId,
    plan_code: props.planCode,
    billing_cycle: props.billingCycle,
    amount: props.amount,
  });
};

export const trackSubscriptionFailed = (props: {
  orgId?: string;
  reason?: string;
}) => {
  track('Subscription Failed', {
    org_id: props.orgId,
    reason: props.reason,
  });
};
